import { flexRender, Header } from "@tanstack/react-table";
import { ModelScreenerRow } from "@models/model-screener/entities/modelScreenerData";
import {
  SHBox,
  SHHtmlBlock,
  SHPopover,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { IconButton, useTheme } from "@mui/material";
import { ArrowDownSVG, FilterClearSVG, FilterSVG } from "@components/svgs";
import React from "react";
import { DELAY_TIME } from "@constants";
import { hexToRGBA } from "@utils";
import { useToggle } from "react-use";
import { MSTableHeaderStyledTooltip } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-head-cell/styled-tooltip";
import { MSTableSortMenu } from "src/pages/managed-accounts/model-screener/components/model-screener-table/table/table-head-cell/sort-menu";
import { MSTableFilterMenu } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-head-cell/filter-menu";
import { SHTextOverflowTooltip } from "@components/design-systems/sh-text-overflow-tooltip";
import { MSTableSelectedAllRowCheckbox } from "@pages/managed-accounts/model-screener/components/model-screener-table/checkboxes/row-select/row-select-all";

type MSTableHeaderPopupProps = {
  anchorEl: HTMLButtonElement | null;
  header: Header<ModelScreenerRow, unknown>;
  isMainHeader: boolean;
};

export const MSTableHeaderPopup = ({
  anchorEl,
  header,
  isMainHeader,
}: MSTableHeaderPopupProps) => {
  const { palette } = useTheme();

  const { column, index } = header;

  const isCanSort = column.getCanSort();
  const isSorted = column.getIsSorted();
  const isCanFilter = column.getCanFilter();
  const isFiltered = column.getIsFiltered();
  const metaData = column.columnDef.meta;
  const flexRenderValue = flexRender(
    column.columnDef.header,
    header.getContext(),
  );

  const [opening, toggleOpen] = useToggle(false);

  return (
    <>
      <SHStack
        spacing={1}
        direction="row"
        alignItems={"center"}
        onClick={() => {
          (isCanSort || isCanFilter) && toggleOpen(true);
        }}
      >
        <MSTableHeaderStyledTooltip
          disableHoverListener={!metaData?.tooltip}
          title={
            <SHStack
              spacing={1}
              sx={{
                p: "15px 15px 10px 15px",
              }}
            >
              <SHTypography
                variant="body2"
                fontSize={14}
                fontWeight={700}
                lineHeight={1.2}
              >
                {metaData?.tooltip?.title}
              </SHTypography>

              <SHHtmlBlock
                variant="body3"
                zIndex={1}
                color={palette.text.disabled}
                content={metaData?.tooltip?.description}
                textAlign={"left"}
              />
            </SHStack>
          }
          enterDelay={DELAY_TIME}
          enterNextDelay={DELAY_TIME}
        >
          <SHStack
            direction={"row"}
            justifyItems={"center"}
            alignItems={"center"}
            marginLeft={metaData?.isSelectColumn ? "-10px" : 0}
          >
            <SHStack onClick={(e) => e.stopPropagation()}>
              {metaData?.isSelectColumn && (
                <MSTableSelectedAllRowCheckbox />
              )}
            </SHStack>
            <SHBox component="span">
              {metaData?.isSelectColumn ? (
                <SHTypography
                  variant="body1"
                  colorVariant="third"
                  lineHeight={isMainHeader ? 1 : 1.2}
                  fontSize={isMainHeader && index === 0 ? 10 : 12}
                  marginX={metaData?.isSelectColumn ? "-7px" : 0}
                >
                  {flexRenderValue}
                </SHTypography>
              ) : (
                <SHTextOverflowTooltip
                  tooltipContent={flexRenderValue}
                  lineClamp={isMainHeader ? 1 : 2}
                  breakAnyWhere={isMainHeader}
                  preventGetFitWidth={isMainHeader}
                >
                  <SHTypography
                    variant="body1"
                    colorVariant="third"
                    lineHeight={isMainHeader ? 1 : 1.2}
                    fontSize={isMainHeader && index === 0 ? 10 : 12}
                    marginX={metaData?.isSelectColumn ? "-7px" : 0}
                  >
                    {flexRenderValue}
                  </SHTypography>
                </SHTextOverflowTooltip>
              )}
            </SHBox>
          </SHStack>
        </MSTableHeaderStyledTooltip>

        {isCanSort && (
          <IconButton
            sx={{ padding: "0px" }}
            onClick={(event: any) => {
              event.stopPropagation();
              if (isSorted === "desc") {
                column.clearSorting();
                return;
              }
              column.toggleSorting(!!isSorted);
            }}
          >
            <SHStack
              alignItems={"center"}
              justifyContent={"center"}
              sx={{
                transition: "all ease-in-out 0.2s",
                transform: `rotate(${isSorted !== "desc" ? 0 : 180}deg)`,
              }}
            >
              <ArrowDownSVG
                color={isSorted ? palette.primary.main : undefined}
                width={"12px"}
                height={"12px"}
                transform="rotate(180)"
              />
            </SHStack>
          </IconButton>
        )}

        {isCanFilter && (
          <IconButton
            data-testid="sh-head-cell-filter-btn"
            sx={{ padding: "0px" }}
            onClick={(event: any) => {
              if (isFiltered) {
                event.stopPropagation();
                column.setFilterValue(undefined);
              }
            }}
          >
            {isFiltered ? (
              <FilterClearSVG width={"16px"} height={"16px"} />
            ) : (
              <FilterSVG width={"15px"} height={"15px"} />
            )}
          </IconButton>
        )}
      </SHStack>

      <SHPopover
        open={opening}
        anchorEl={anchorEl}
        onClose={() => toggleOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            boxShadow: "none",
            borderWidth: 1,
            borderColor: palette.divider,
            borderStyle: "solid",
            marginLeft: "-1px",
            marginTop: "-1px",
            borderRadius: 0,
            backgroundColor: hexToRGBA(palette.common.white, 0.95),
            width: "250px",
            filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
          },
        }}
      >
        {isCanSort && <MSTableSortMenu column={column} />}
        {isCanFilter && <MSTableFilterMenu column={column} />}
      </SHPopover>
    </>
  );
};
